import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { format as formatDate, parseISO as parseISODate } from "date-fns";
import { dateFnsOptions } from "../../../_utils/dateFnsLocale";
import SocialAccountPropType from "../../../_types/SocialAccount";
import PostPropType from "../../../_types/Post";
import * as SocialProviders from "../../../_constants/SocialProvider";
import * as PostAdditionalTypes from "../../../_constants/PostAdditionalTypes";
import * as PostTypes from "../../../_constants/PostTypes";
import { getSocialProviderBySocialType } from "../../../_utils/socialProvider";
import Alert from "../Alert/Alert";
import SocialPostCardFacebook from "./SocialPostCardFacebook";
import SocialPostCardTwitter from "./SocialPostCardTwitter";
import SocialPostCardLinkedIn from "./SocialPostCardLinkedIn";
// import SocialPostCardVKontakte from "./SocialPostCardVKontakte";
import SocialPostCardGoogle from "./SocialPostCardGoogle";
import SocialPostCardYouTube from "./SocialPostCardYouTube";
import SocialPostCardYouTubeShort from "./SocialPostCardYouTubeShort";
import SocialPostCardPinterest from "./SocialPostCardPinterest";
import SocialPostCardInstagram from "./SocialPostCardInstagram";
import SocialPostCardInstagramStory from "./SocialPostCardInstagramStory";
import SocialPostCardInstagramReel from "./SocialPostCardInstagramReel";
import SocialPostCardTikTok from "./SocialPostCardTikTok";

const SocialPostCard = ({ account, post, ...props }) => {
  const { t } = useTranslation(["planner"]);
  const type = account?.social_type;
  const time = useMemo(
    () =>
      post?.date && post?.time
        ? `${formatDate(
            parseISODate(`${post.date} ${post.time}`),
            "LLL do",
            dateFnsOptions()
          )}`
        : "1 min",
    [post.date, post.time]
  );
  const timeFull = useMemo(
    () =>
      post?.date && post?.time
        ? `${formatDate(
            parseISODate(`${post.date} ${post.time}`),
            "LLL do, yyyy, p",
            dateFnsOptions()
          )}`
        : "1 min",
    [post.date, post.time]
  );
  const postType = useMemo(() => post?.additional?.find((item) => item.type === PostAdditionalTypes.POST_TYPE)?.value, [post?.additional]);

  if (!account || !post || !type) {
    return null;
  }

  const provider = getSocialProviderBySocialType(type);

  switch (provider) {
    case SocialProviders.FACEBOOK:
      return (
        <SocialPostCardFacebook
          account={account}
          post={post}
          time={timeFull}
          {...props}
        />
      );
    case SocialProviders.TWITTER:
      return (
        <SocialPostCardTwitter
          account={account}
          post={post}
          time={time}
          timeFull={timeFull}
          {...props}
        />
      );
    case SocialProviders.LINKEDIN:
      return (
        <SocialPostCardLinkedIn
          account={account}
          post={post}
          time={timeFull}
          {...props}
        />
      );
    // case SocialProviders.VKONTAKTE:
    //   return (
    //     <SocialPostCardVKontakte
    //       account={account}
    //       post={post}
    //       time={timeFull}
    //       {...props}
    //     />
    //   );
    case SocialProviders.GOOGLE:
      return (
        <SocialPostCardGoogle
          account={account}
          post={post}
          time={timeFull}
          {...props}
        />
      );
    case SocialProviders.YOUTUBE:
      switch (postType) {
        case PostTypes.SHORT:
          return (
            <SocialPostCardYouTubeShort
              account={account}
              post={post}
              time={timeFull}
              {...props}
            />
          );
        case PostTypes.VIDEO:
        default:
          return (
            <SocialPostCardYouTube
              account={account}
              post={post}
              time={timeFull}
              {...props}
            />
          );
      }
    case SocialProviders.PINTEREST:
      return (
        <SocialPostCardPinterest
          account={account}
          post={post}
          time={time}
          {...props}
        />
      );
    case SocialProviders.INSTAGRAM:
      switch (postType) {
        case PostTypes.STORY:
          return (
            <SocialPostCardInstagramStory
              account={account}
              post={post}
              time={timeFull}
              {...props}
            />
          );
          case PostTypes.REEL:
          return (
            <SocialPostCardInstagramReel
              account={account}
              post={post}
              time={timeFull}
              {...props}
            />
          );
        case PostTypes.POST:
        default:
          return (
            <SocialPostCardInstagram
              account={account}
              post={post}
              time={timeFull}
              {...props}
            />
          );
      }
    case SocialProviders.TIKTOK:
      return (
        <SocialPostCardTikTok
          account={account}
          post={post}
          time={time}
          {...props}
        />
      );
    default:
      return (
        <Alert status="warning" showIcon>
          {t("post_preview.not_available")}
        </Alert>
      );
  }
};

SocialPostCard.propTypes = {
  account: SocialAccountPropType.isRequired,
  post: PostPropType.isRequired,
};

SocialPostCard.defaultProps = {};

export default SocialPostCard;
